(() => {
  const SECTION_SELECTOR = '[data-activity-waterfall-section]';
  const GRID_SELECTOR = '[data-activity-waterfall-grid]';
  const ITEM_SELECTOR = '[data-activity-waterfall-item]';
  const TRIGGER_SELECTOR = '[data-activity-preview-trigger]';
  const MEDIA_SELECTOR = '[data-activity-preview-media]';
  const DETAIL_SELECTOR = 'template[data-activity-detail]';
  const PREVIEW_SELECTOR = '[data-activity-preview]';

  const instances = new WeakMap();

  const getColumnCount = () => (window.matchMedia('(min-width: 750px)').matches ? 3 : 1);

  class ActivityWaterfall {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector(GRID_SELECTOR);
      this.items = [...section.querySelectorAll(ITEM_SELECTOR)];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeItem = null;
      this.activeTrigger = null;
      this.flyer = null;
      this.isOpen = false;

      this.layout = this.layout.bind(this);
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onImageSettled = this.onImageSettled.bind(this);
      this.onPreviewScroll = this.onPreviewScroll.bind(this);
      this.onPreviewWheel = this.onPreviewWheel.bind(this);

      if (!this.grid || !this.items.length) return;

      this.enhanceGrid();
      this.setupPreview();
    }

    enhanceGrid() {
      this.grid.classList.add('is-enhanced');
      this.images = this.items
        .map((item) => item.querySelector(`${MEDIA_SELECTOR} img`))
        .filter(Boolean);

      this.images.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.onImageSettled);
        image.addEventListener('error', this.onImageSettled);
      });

      this.resizeObserver = new ResizeObserver(this.layout);
      this.resizeObserver.observe(this.grid);
      window.addEventListener('resize', this.layout);
      document.fonts?.ready.then(this.layout).catch(() => {});

      requestAnimationFrame(this.layout);
    }

    setupPreview() {
      this.preview = this.section.querySelector(PREVIEW_SELECTOR);
      if (!this.preview) return;

      this.previewBackdrop = this.preview.querySelector('[data-activity-preview-backdrop]');
      this.previewClose = this.preview.querySelector('[data-activity-preview-close]');
      this.previewDialog = this.preview.querySelector('[data-activity-preview-dialog]');
      this.previewMedia = this.preview.querySelector('[data-activity-preview-media-target]');
      this.previewBody = this.preview.querySelector('[data-activity-preview-body]');
      this.previewScroller = this.preview.querySelector('[data-activity-preview-scroller]');
      this.previewTitle = this.preview.querySelector('[data-activity-preview-title]');
      this.previewContent = this.preview.querySelector('[data-activity-preview-content]');
      this.previewTime = this.preview.querySelector('[data-activity-preview-time]');

      document.body.append(this.preview);

      this.items.forEach((item) => {
        const trigger = item.querySelector(TRIGGER_SELECTOR);
        trigger?.addEventListener('click', this.onTriggerClick);
      });

      this.previewBackdrop?.addEventListener('click', this.onBackdropClick);
      this.previewClose?.addEventListener('click', this.onCloseClick);
      this.preview.addEventListener('wheel', this.onPreviewWheel, { passive: false });
      this.previewScroller?.addEventListener('scroll', this.onPreviewScroll, { passive: true });
      document.addEventListener('keydown', this.onKeydown);
    }

    layout() {
      if (!this.grid || !this.items.length) return;

      const gridWidth = this.grid.clientWidth;
      if (!gridWidth) return;

      const columnCount = getColumnCount();
      const gap = 8;
      const columnWidth = (gridWidth - gap * (columnCount - 1)) / columnCount;
      const columnHeights = new Array(columnCount).fill(0);

      this.items.forEach((item) => {
        item.style.boxSizing = 'border-box';
        item.style.left = '0px';
        item.style.position = 'absolute';
        item.style.top = '0px';
        item.style.width = `${columnWidth}px`;
      });

      this.items.forEach((item) => {
        const columnIndex = columnHeights.indexOf(Math.min(...columnHeights));
        const x = columnIndex * (columnWidth + gap);
        const y = columnHeights[columnIndex];

        item.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        columnHeights[columnIndex] += item.offsetHeight + gap;
      });

      this.grid.style.height = `${Math.max(...columnHeights) - gap}px`;
    }

    onImageSettled() {
      this.layout();
    }

    onTriggerClick(event) {
      if (!window.gsap) return;

      event.preventDefault();
      const trigger = event.currentTarget;
      const item = trigger.closest(ITEM_SELECTOR);
      this.open(item, trigger);
    }

    onBackdropClick() {
      this.close();
    }

    onCloseClick() {
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    onPreviewScroll() {
      if (!this.previewScroller) return;

      this.previewScroller.classList.add('is-scrolling');
      window.clearTimeout(this.scrollStopTimer);
      this.scrollStopTimer = window.setTimeout(() => {
        this.previewScroller.classList.remove('is-scrolling');
      }, 650);
    }

    onPreviewWheel(event) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const scrollContainer = isMobile ? this.previewDialog : this.previewScroller;

      if (!scrollContainer?.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    lockPageScroll() {
      document.documentElement.classList.add('activity-waterfall-open');
      document.body.classList.add('activity-waterfall-open');
      window.desviewLenis?.stop();
    }

    unlockPageScroll() {
      document.documentElement.classList.remove('activity-waterfall-open');
      document.body.classList.remove('activity-waterfall-open');
      window.desviewLenis?.start();
    }

    populateDetail(item) {
      const detail = item.querySelector(DETAIL_SELECTOR);
      const root = detail?.content;
      const title = root?.querySelector('[data-activity-detail-title]');
      const content = root?.querySelector('[data-activity-detail-content]');
      const time = root?.querySelector('[data-activity-detail-time]');

      this.previewTitle.innerHTML = title ? title.innerHTML : '';
      this.previewContent.innerHTML = content ? content.innerHTML : '';
      this.previewTime.innerHTML = time ? time.innerHTML : '';
      this.previewTime.hidden = !time;
    }

    createPreviewImage(sourceImage) {
      const image = document.createElement('img');
      image.alt = sourceImage.alt || '';
      image.className = 'activity-waterfall-preview__image';
      image.decoding = 'async';
      image.loading = 'eager';

      if (sourceImage.srcset) {
        image.srcset = sourceImage.srcset;
        image.sizes = window.matchMedia('(max-width: 749px)').matches ? '100vw' : '52vw';
      }

      image.src = sourceImage.currentSrc || sourceImage.src;
      return image;
    }

    createFlyer(sourceImage, rect) {
      const flyer = document.createElement('div');
      flyer.className = 'activity-waterfall-preview__flyer';
      flyer.style.height = `${rect.height}px`;
      flyer.style.left = `${rect.left}px`;
      flyer.style.top = `${rect.top}px`;
      flyer.style.width = `${rect.width}px`;
      flyer.append(this.createPreviewImage(sourceImage));
      return flyer;
    }

    getPreviewImageRect(sourceImage) {
      const bounds = this.previewMedia.getBoundingClientRect();
      const ratio = sourceImage.naturalWidth && sourceImage.naturalHeight
        ? sourceImage.naturalWidth / sourceImage.naturalHeight
        : bounds.width / bounds.height;
      let width = bounds.height * ratio;
      let height = bounds.height;

      if (width > bounds.width) {
        width = bounds.width;
        height = width / ratio;
      }

      return {
        height,
        left: bounds.left + (bounds.width - width) / 2,
        top: bounds.top + (bounds.height - height) / 2,
        width
      };
    }

    open(item, trigger) {
      if (this.isOpen || !item || !window.gsap) return;

      const media = item.querySelector(MEDIA_SELECTOR);
      const sourceImage = media?.querySelector('img');
      const detail = item.querySelector(DETAIL_SELECTOR);

      if (!media || !sourceImage || !detail) {
        window.location.href = trigger.href;
        return;
      }

      this.isOpen = true;
      this.activeItem = item;
      this.activeTrigger = trigger;
      item.classList.add('is-active');

      this.populateDetail(item);
      this.previewMedia.innerHTML = '';
      const previewImage = this.createPreviewImage(sourceImage);
      this.previewMedia.append(previewImage);
      this.previewMedia.style.visibility = 'hidden';
      this.previewScroller.scrollTop = 0;
      if (this.previewDialog) this.previewDialog.scrollTop = 0;

      this.preview.setAttribute('aria-hidden', 'false');
      this.lockPageScroll();

      const reduceMotion = this.reducedMotion.matches;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const gsap = window.gsap;

      gsap.killTweensOf([this.previewBackdrop, this.previewBody, this.previewClose, this.flyer].filter(Boolean));
      gsap.set(this.preview, { autoAlpha: 1 });
      gsap.set(this.previewBackdrop, { backgroundColor: 'rgba(255, 255, 255, 0.97)' });
      gsap.set(this.previewBody, { autoAlpha: 0, x: reduceMotion ? 0 : 36 });
      gsap.set(this.previewClose, { autoAlpha: 0 });

      if (isMobile) {
        this.previewMedia.style.visibility = '';
        gsap.set(previewImage, { clipPath: 'inset(100% 0 0 0)' });
        gsap.set(this.previewBody, { autoAlpha: 0, x: 0, y: reduceMotion ? 0 : 24 });

        if (reduceMotion) {
          gsap.set(previewImage, { clipPath: 'inset(0% 0 0 0)' });
          gsap.set([this.previewBody, this.previewClose], { autoAlpha: 1, x: 0, y: 0 });
          this.previewClose.focus({ preventScroll: true });
          return;
        }

        gsap.timeline()
          .to(previewImage, {
            clipPath: 'inset(0% 0 0 0)',
            duration: 0.65,
            ease: 'power3.inOut'
          }, 0)
          .to(this.previewClose, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0.38)
          .to(this.previewBody, {
            autoAlpha: 1,
            y: 0,
            duration: 0.38,
            ease: 'power2.out',
            onComplete: () => this.previewClose.focus({ preventScroll: true })
          }, 0.32);
        return;
      }

      const finishOpen = () => {
        this.previewMedia.style.visibility = '';
        this.flyer?.remove();
        this.flyer = null;
        this.previewClose.focus({ preventScroll: true });
      };

      if (reduceMotion) {
        gsap.set(this.previewBackdrop, { backgroundColor: 'rgba(255, 255, 255, 0.97)' });
        gsap.set([this.previewBody, this.previewClose], { autoAlpha: 1, x: 0 });
        finishOpen();
        return;
      }

      const sourceRect = media.getBoundingClientRect();
      this.flyer = this.createFlyer(sourceImage, sourceRect);
      this.preview.append(this.flyer);

      const targetRect = this.getPreviewImageRect(sourceImage);
      const timeline = gsap.timeline({ defaults: { ease: 'power3.inOut' }, onComplete: finishOpen });

      timeline
        .to(this.flyer, {
          height: targetRect.height,
          left: targetRect.left,
          top: targetRect.top,
          width: targetRect.width,
          duration: 0.58
        }, 0)
        .to(this.previewClose, { autoAlpha: 1, duration: 0.2, ease: 'power2.out' }, 0.38)
        .to(this.previewBody, { autoAlpha: 1, x: 0, duration: 0.38, ease: 'power2.out' }, 0.32);
    }

    close(immediate = false) {
      if (!this.isOpen || !window.gsap) return;

      const item = this.activeItem;
      const trigger = this.activeTrigger;
      const media = item?.querySelector(MEDIA_SELECTOR);
      const gsap = window.gsap;
      const reduceMotion = this.reducedMotion.matches;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      const finishClose = () => {
        this.flyer?.remove();
        this.flyer = null;
        this.previewMedia.style.visibility = '';
        gsap.set(this.preview, { autoAlpha: 0 });
        this.preview.setAttribute('aria-hidden', 'true');
        this.unlockPageScroll();
        item?.classList.remove('is-active');
        trigger?.focus({ preventScroll: true });
        this.activeItem = null;
        this.activeTrigger = null;
        this.isOpen = false;
      };

      if (immediate || reduceMotion || !media) {
        finishClose();
        return;
      }

      if (isMobile) {
        const previewImage = this.previewMedia.querySelector('img');
        gsap.killTweensOf([previewImage, this.previewBody, this.previewClose].filter(Boolean));
        gsap.timeline({ onComplete: finishClose })
          .to([this.previewBody, this.previewClose], {
            autoAlpha: 0,
            duration: 0.2,
            ease: 'power2.in'
          }, 0)
          .to(previewImage, {
            clipPath: 'inset(100% 0 0 0)',
            duration: 0.45,
            ease: 'power3.inOut'
          }, 0);
        return;
      }

      const sourceRect = media.getBoundingClientRect();

      if (this.flyer) {
        gsap.killTweensOf(this.flyer);
      } else {
        const currentImage = this.previewMedia.querySelector('img');
        if (!currentImage) {
          finishClose();
          return;
        }
        this.flyer = this.createFlyer(currentImage, currentImage.getBoundingClientRect());
        this.preview.append(this.flyer);
        this.previewMedia.style.visibility = 'hidden';
      }

      gsap.killTweensOf([this.previewBackdrop, this.previewBody, this.previewClose]);

      const timeline = gsap.timeline({ defaults: { ease: 'power3.inOut' }, onComplete: finishClose });

      timeline
        .to([this.previewBody, this.previewClose], { autoAlpha: 0, x: 24, duration: 0.24, ease: 'power2.in' }, 0)
        .to(this.previewBackdrop, { backgroundColor: 'rgba(0, 0, 0, 0)', duration: 0.32, ease: 'power2.out' }, 0)
        .to(this.flyer, {
          height: sourceRect.height,
          left: sourceRect.left,
          top: sourceRect.top,
          width: sourceRect.width,
          duration: 0.42
        }, 0);
    }

    destroy() {
      window.removeEventListener('resize', this.layout);
      this.resizeObserver?.disconnect();

      this.images?.forEach((image) => {
        image.removeEventListener('load', this.onImageSettled);
        image.removeEventListener('error', this.onImageSettled);
      });

      this.items.forEach((item) => {
        const trigger = item.querySelector(TRIGGER_SELECTOR);
        trigger?.removeEventListener('click', this.onTriggerClick);
      });

      this.previewBackdrop?.removeEventListener('click', this.onBackdropClick);
      this.previewClose?.removeEventListener('click', this.onCloseClick);
      this.preview?.removeEventListener('wheel', this.onPreviewWheel);
      this.previewScroller?.removeEventListener('scroll', this.onPreviewScroll);
      document.removeEventListener('keydown', this.onKeydown);
      window.clearTimeout(this.scrollStopTimer);

      if (this.preview?.parentNode === document.body) {
        this.preview.remove();
      }

      this.unlockPageScroll();
    }
  }

  const init = (root = document) => {
    const sections = root.matches?.(SECTION_SELECTOR)
      ? [root, ...root.querySelectorAll(SECTION_SELECTOR)]
      : [...root.querySelectorAll(SECTION_SELECTOR)];

    sections.forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new ActivityWaterfall(section));
    });
  };

  const destroy = (root = document) => {
    const sections = root.matches?.(SECTION_SELECTOR)
      ? [root, ...root.querySelectorAll(SECTION_SELECTOR)]
      : [...root.querySelectorAll(SECTION_SELECTOR)];

    sections.forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  };

  init();
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
